import React, { useEffect, useState, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Checkbox } from "sr/helpers";
import { getNearbyRegions } from "sr/utils/api/region/getNearbyRegions";

export const AdditionalRegionsChange = (props: any) => {
  // Safe extraction of primaryRegionId (bypassing the [object Object] bug)
  const rawLoc = props?.data?.job_advanced_id?.reporting_address;
  let primaryRegionId = "";
  if (rawLoc) {
    if (typeof rawLoc === "string") primaryRegionId = rawLoc;
    else if (typeof rawLoc.regionId === "string") primaryRegionId = rawLoc.regionId;
    else if (typeof rawLoc.regionId?._id === "string") primaryRegionId = rawLoc.regionId._id;
    else if (typeof rawLoc.regionId?.id === "string") primaryRegionId = rawLoc.regionId.id;
    else if (typeof rawLoc.region_id === "string") primaryRegionId = rawLoc.region_id;
    else if (typeof rawLoc.region?.id === "string") primaryRegionId = rawLoc.region.id;
    else if (typeof rawLoc.region?._id === "string") primaryRegionId = rawLoc.region._id;
    else if (typeof rawLoc.region === "string") primaryRegionId = rawLoc.region;
  }

  const [additionalRegions, setAdditionalRegions] = useState<string[]>([]);
  const [nearbyRegions, setNearbyRegions] = useState<any[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);

  useEffect(() => {
    const ids = Array.isArray(props?.data?.job_advanced_id?.reporting_regions)
      ? props.data.job_advanced_id.reporting_regions.map((r: any) => String(r?.id || r?._id || r || "")).filter(Boolean)
      : [];
    setAdditionalRegions(ids);
  }, [props?.data, primaryRegionId]);

  useEffect(() => {
    props?.HandleDataProps?.({ reporting_regions: additionalRegions });
  }, [additionalRegions]);

  const loadNearby = async (limit: number) => {
    if (!primaryRegionId) return;
    setNearbyLoading(true);
    try {
      const res = await getNearbyRegions({
        id: primaryRegionId,
        page: 1,
        limit,
        radius: 50,
      });
      if (res?.success) {
        const results = Array.isArray(res.data) ? res.data : [];
        setNearbyRegions(results);
        if (results.length < limit) {
          setAllLoaded(true);
        }
      } else {
        setNearbyRegions([]);
        setAllLoaded(true);
      }
    } catch {
      setNearbyRegions([]);
      setAllLoaded(true);
    } finally {
      setNearbyLoading(false);
    }
  };

  useEffect(() => {
    setNearbyRegions([]);
    setAllLoaded(false);
    if (primaryRegionId) {
      loadNearby(10);
    }
  }, [primaryRegionId]);

  const toggleRegion = (id: string) => {
    const list = [...additionalRegions];
    if (list.includes(id)) {
      setAdditionalRegions(list.filter((x) => x !== id));
    } else {
      setAdditionalRegions([...list, id]);
    }
  };

  const removeRegion = (id: string) => {
    setAdditionalRegions(additionalRegions.filter((x) => x !== id));
  };

  // Helper for rendering selected chips
  const selectedChips = useMemo(() => {
    // For chips, we try to match against loaded nearbyRegions. 
    // If not found (e.g. from a past selection), we just display the ID or fetch it.
    // For simplicity, we just use the ID if we don't have the city name locally, 
    // since this is a quick revert.
    return additionalRegions.map((id) => {
      const found = nearbyRegions.find((r) => String(r.id) === id);
      const label = found ? `${found.city}${found.state ? ` (${found.state})` : ""}${found.distanceMiles != null ? `, ${found.distanceMiles} mi` : ""}` : id;
      return { id, label };
    });
  }, [additionalRegions, nearbyRegions]);

  if (!primaryRegionId) {
    return (
      <div className="pt-4 text-center text-neutral-500">
        <p>No primary reporting location found for this job.</p>
        <p className="text-sm">Please set the reporting address first.</p>
      </div>
    );
  }

  return (
    <div className="pt-4 max-h-[55vh] overflow-y-auto pr-1">
      <div className="mt-4 max-h-[32vh] overflow-auto divide-y divide-neutral-200">
        {nearbyLoading && nearbyRegions.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500 text-center">
            <FormattedMessage id="LOADING" defaultMessage="Loading..." />
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-4">
            {nearbyRegions.map((r: any) => {
              const id = String(r?.id || "");
              if (!id || id === primaryRegionId) return null;
              const selected = additionalRegions.includes(id);
              return (
                <Checkbox
                  key={id}
                  id={`additional-region-${id}`}
                  value={id}
                  checked={selected}
                  inputClass="accent-[#ff39b3] focus:ring-0 focus:ring-transparent focus:outline-none"
                  label={
                    <span className={`${selected ? "font-bold" : "font-medium"} text-sm text-gray-900`}>
                      {r.city}{r.state ? ` (${r.state})` : ""}{r.distanceMiles != null ? `, ${r.distanceMiles} mi` : ""}
                    </span>
                  }
                  onChange={() => toggleRegion(id)}
                />
              );
            })}
          </div>
        )}

        {!nearbyLoading && (
          <div className="mt-2 flex justify-center py-4">
            <button
              type="button"
              disabled={allLoaded}
              onClick={() => { if (!allLoaded) loadNearby(50); }}
              className={`ring-1 rounded px-2 py-1 text-sm ${allLoaded ? "text-gray-400 ring-gray-300 cursor-not-allowed" : "text-[#ff39b3] ring-[#ff39b3] hover:bg-[#ff39b3] hover:text-white"}`}
            >
              <FormattedMessage id="VIEW.ALL" defaultMessage="View All" />
            </button>
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-neutral-200">
        <div className="text-sm font-bold text-gray-900">
          <FormattedMessage id="INPUT.SELECTED.REGIONS" defaultMessage="Selected regions" />
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedChips.length ? (
            selectedChips.map((c) => (
              <span
                key={`selected-region-chip-${c.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-neutral-100 border border-neutral-200 px-3 py-1 text-sm text-gray-900"
                title={c.label}
              >
                <span className="truncate max-w-[260px]">{c.label}</span>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-800 leading-none"
                  onClick={() => removeRegion(c.id)}
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-700">-</span>
          )}
        </div>
      </div>
    </div>
  );
};
